export const normalizeCogniveId = (value) => {
    if (value == null) return null;
    const trimmed = String(value).trim();
    return trimmed.length > 0 ? trimmed : null;
};

export const OPTIMIZER_DISPLAY_IDENTITY_FIELDS = Object.freeze([
    'company_name',
    'inbound_id_type',
    'inbound_id_value',
    'msi_listing_label',
    'uploaded_ticker',
    'yf_longname',
    'yf_shortname',
]);

export const OPTIMIZER_RISK_PRESENTATION_FIELDS = Object.freeze([
    'factorRiskDollar',
    'idioRiskDollar',
    'idio_vol_annual_custom',
    'idio_vol_linked',
    'factorVol',
]);

const hasDisplayIdentityValue = (value) => (
    value != null && (typeof value !== 'string' || value.trim() !== '')
);

const preserveOptimizerFields = ({ rows, sources, fields, hasValue }) => {
    const sourcesByCogniveId = new Map();
    sources.flat().forEach((row) => {
        const cogniveId = normalizeCogniveId(row?.cognive_id);
        if (!cogniveId) return;
        const matchingSources = sourcesByCogniveId.get(cogniveId) || [];
        matchingSources.push(row);
        sourcesByCogniveId.set(cogniveId, matchingSources);
    });

    return (rows || []).map((row) => {
        const matchingSources = sourcesByCogniveId.get(
            normalizeCogniveId(row?.cognive_id),
        ) || [];
        const preservedFields = Object.fromEntries(
            fields.flatMap((field) => {
                if (hasValue(row?.[field])) return [];
                const source = matchingSources.find((candidate) => (
                    hasValue(candidate?.[field])
                ));
                return source ? [[field, source[field]]] : [];
            }),
        );
        return { ...row, ...preservedFields };
    });
};

export const preserveOptimizerDisplayIdentity = (rows, ...identitySources) => (
    preserveOptimizerFields({
        rows,
        sources: identitySources,
        fields: OPTIMIZER_DISPLAY_IDENTITY_FIELDS,
        hasValue: hasDisplayIdentityValue,
    })
);

export const preserveOptimizerRiskPresentation = (rows, ...riskSources) => (
    preserveOptimizerFields({
        rows,
        sources: riskSources,
        fields: OPTIMIZER_RISK_PRESENTATION_FIELDS,
        hasValue: (value) => (
            value != null
            && value !== ''
            && Number.isFinite(Number(value))
        ),
    })
);

export const parseCogniveIdFromFactorIdentity = (value) => {
    const normalized = normalizeCogniveId(value);
    if (!normalized) return null;
    if (normalized.startsWith('c:')) {
        const token = normalized.slice(2).trim();
        return token.startsWith('cog_') ? token : null;
    }
    return normalized.startsWith('cog_') ? normalized : null;
};

export const getRowCogniveId = (row) => {
    if (!row || typeof row !== 'object') return null;

    const directCandidates = [
        row.cognive_id,
        row.COGNIVE_ID,
        row.security_id,
        row.SECURITY_ID,
    ];

    for (const candidate of directCandidates) {
        const normalized = normalizeCogniveId(candidate);
        if (normalized && normalized.startsWith('cog_')) {
            return normalized;
        }
    }

    const factorIdentityCandidates = [
        row.factor_identity,
        row.FACTOR_IDENTITY,
        row.identity_key,
    ];

    for (const candidate of factorIdentityCandidates) {
        const parsed = parseCogniveIdFromFactorIdentity(candidate);
        if (parsed) return parsed;
    }

    const idCandidate = normalizeCogniveId(row.id);
    if (idCandidate && idCandidate.startsWith('cog_')) {
        return idCandidate;
    }

    return null;
};
